import { currentFt3dSceneWindow } from '../../ft3d-hud/scene-frame';
import {
    FT3D_AOE_PLACEMENT_SCHEMA,
    parseFt3dAoePlacementStageEnvelope,
    type Ft3dAoePlacementEnvelope,
    type Ft3dAoePlacementStageEnvelope,
    type Ft3dAoePoint,
    type Ft3dAoeShape,
} from '../../shared/ft3d-aoe-placement-contract';
import { getRangeDistanceFromCatalog } from '../aoe-helpers';
import { creatureReachFt } from '../../shared/placed-aoe-range';
import { aoeExcludesCaster, FT_SELF_RANGE_ID } from '../../shared/aoe-caster-exclusion';
import { parseProjectilePool, type ProjectileAssignment } from '../../shared/projectile-targeting';
import type { TeleportPlacementRequest } from '../../shared/teleport-placement';
import { armAoePropagation } from '../../shared/aoe-propagation-wire';
import { armPaintedWall } from '../../shared/painted-wall-wire';
import { isSpellGrantMode } from '../../janus-engine/ability/spell-grant-mode';
import { fastNextSpell, nextSpellNumericTotal, nextSpellBoostLevels, widenNextSpellDimension as widenAoeDimensionFt } from '../../janus-engine/sanguine/next-spell';

function ft3dAoeExcludesCaster(button: HTMLElement): boolean {
    return button.dataset.ftAoeExcludesCaster === '1' || aoeExcludesCaster({
        range: button.dataset.range,
        shape: button.dataset.aoe_type,
        rollType: button.dataset.roll_type,
        itemType: button.dataset.item_type,
        dice: button.dataset.dice,
        die: button.dataset.die,
        persistent: button.dataset.persistent_aoe,
        followsTarget: button.dataset.aoe_follows_target,
    });
}
/** The placement verdict, decided in the browser from the FT3D stage. There is no server pre-check (owner
 * 2026-09-10): the stage already proves range, line of effect and the Self anchor (it pins centered Self
 * areas to the caster's persisted position). Teleport range, clear space and headroom are browser-stage
 * verdicts; the offer callback computes its due settlement disclosures before the raw save.
 * The stage also refuses aim points hidden above the current cut height, a view-local placement constraint. */
function placementFailure(active: FT3DActiveAOE, stage: Ft3dAoePlacementStageEnvelope): string | null {
    const intent = active.envelope.intent;
    if (intent?.wallAttachment && !stage.wallGeometry) return stage.aimFailure ?? 'Paint a wall in the scene.';
    // A GM creature drop has no caster to be in range of or to see from — any placed floor point is valid;
    // the insert endpoint seats the creature on the floor authored there.
    if (intent?.placementKind === 'creature') return stage.targetSelection ? null : 'Choose a location in the scene.';
    // A GM zone edit (MV2 S1b) moves a zone that already exists: no caster range, sight or line of effect applies.
    if (intent?.placementKind === 'zone-edit') return stage.targetSelection ? null : 'Choose a location in the scene.';
    if (intent?.placementKind === 'teleport') {
        // FT3D world points use range and physical landing clearance, never the legacy 2D map bounds.
        // The offer callback discloses due escape-status settlement; teleport placement is blind.
        if (stage.landingFailure === 'Choose another location.') return stage.landingFailure;
        if (!stage.inRange) return 'Out of range · choose another spot';
        return stage.landingFailure ?? stage.aimFailure ?? null;
    }
    // ft_aoe_placement_failure() judges aim visibility before range and line of effect. The scene has already slid the
    // point back to the farthest visible spot (best fit), so this only fires when nothing on the line is visible.
    if (stage.aimFailure) return stage.aimFailure;
    if (!stage.inRange) return 'Out of range · choose another spot';
    if (stage.lineOfEffect === 'blocked') return 'Blocked · choose another spot';
    if (!stage.targetSelection) return 'Choose a location in the scene.';
    return null;
}

/** Mirrors FT_SUMMON_POINTS_MAX (janus_summon.php): the most summons one placement positions individually. */
const FT_SUMMON_POINTS_MAX = 50;

const SLOW_CASTING_TIMES = new Set(['one_minute', 'ten_minutes', 'one_hour', 'eight_hours', 'twelve_hours', 'twenty_four_hours']);

/** A button's `data-range` is a RangeType id (catalog band) or, for a cast whose range Janus computes per
 *  throw (Throw Object: Body × size × weight), plain feet. Reach uses the caster's compiled total. */
function rangeFtFromButton(rangeId: string, rangeTypes: unknown, casterId: string): number | null {
    if (/^\d+(\.\d+)?$/.test(rangeId)) return Number(rangeId);
    const catalogRange = getRangeDistanceFromCatalog(rangeId, rangeTypes as Parameters<typeof getRangeDistanceFromCatalog>[1]);
    return catalogRange === Number.POSITIVE_INFINITY ? null
        : catalogRange === 'reach' ? creatureReachFt(window.FateTide?.casterCache?.get(casterId), ftLayerValue) ?? 0
            : typeof catalogRange === 'number' && Number.isFinite(catalogRange) ? catalogRange : 0;
}

function casterNextSpellArea(button: HTMLElement, casterId: string): { percent: number; boost: number } {
    const empty = { percent: 0, boost: 0 };
    const cache = window.FateTide?.casterCache as Map<string, unknown> | undefined;
    const caster = cache?.get(casterId) ?? cache?.get(casterId.toUpperCase());
    if (!caster || typeof caster !== 'object') return empty;
    const ability = window.ftFindFullNestedItem?.(String(button.dataset.ability_id ?? '').toUpperCase()) as Record<string, unknown> | null;
    if (!ability) return empty;
    const read = { value: (row: Record<string, unknown> | null, key: string) => ftLayerValue(row, key),
        has: (row: Record<string, unknown> | null, key: string) => ftLayerValue(row, key) != null,
        statuses: (row: Record<string, unknown>) => ftCreaturePayloadStatuses(row) as Record<string, unknown>[],
        definition: (id: string) => (window as Window & {
            ftResolveAbilityDefinition?: (id: string) => Record<string, unknown> | null;
        }).ftResolveAbilityDefinition?.(id) ?? window.ftFindFullNestedItem?.(id) as Record<string, unknown> | null };
    if (!fastNextSpell(read, ability)) return empty;
    return { percent: nextSpellNumericTotal(read, caster as Record<string, unknown>, 'next_spell_aoe_changed_by_percentage', 300),
        boost: nextSpellBoostLevels(read, caster as Record<string, unknown>, ability) };
}

export interface FT3DActiveAOE {
    teleportOfferId?: string;
    button: HTMLElement;
    envelope: Ft3dAoePlacementEnvelope;
    staged: Ft3dAoePlacementStageEnvelope | null;
    placementCheck?: { key: string; state: 'valid' | 'invalid'; message: string };
    castOverride?: ((point: Ft3dAoePlacementStageEnvelope['center']) => void) | null;
    projectiles?: Ft3dAoePlacementStageEnvelope[];
    /** A multi-summon cast (owner 2026-09-16): the spots already chosen, one per summon, and how many it summons.
     *  Each confirm banks the staged spot; the confirm that banks the last one casts. */
    summonPoints?: Ft3dAoePoint[];
    summonCount?: number;
    /** The count is rolled: summonCount is the roll's maximum and the caster may cast before banking them all. */
    summonRolled?: boolean;
    /** Told when the placement ends WITHOUT a commit (Esc, Cancel, or another placement replacing it). */
    onCancel?: (() => void) | null;
    committed?: boolean;
}

/** MV2 S1b: a GM moving an already-placed persistent zone (its stored span + dimensions, from the encounter cache). */
export interface Ft3dZoneEditRequest {
    encounterId: string;
    statusId: string;
    name: string;
    shape: Ft3dAoeShape;
    distanceFt: number;
    heightFt: number;
    depthFt: number;
    origin: Ft3dAoePoint;
    endpoint: Ft3dAoePoint;
    onCancel?: () => void;
}

/** A GM dropping an encounter creature from the add-creature picker (owner 2026-09-11). */
export interface Ft3dCreaturePlacementRequest {
    encounterId: string;
    creatureId: string;
    name: string;
    pictureUrl: string;
    widthFt: number;
    heightFt: number;
    place: (point: Ft3dAoePoint) => void;
    onCancel?: () => void;
}

declare global {
    interface Window {
        ftStartCreaturePlacement?: (request: Ft3dCreaturePlacementRequest) => boolean;
    }
}

export interface Ft3dSummonPlacementRequest {
    creatureId: string;
    name: string;
    pictureUrl: string;
    widthFt: number;
    heightFt: number;
    /** How many creatures this cast summons when the count is known before the roll; each gets its own spot. */
    count?: number;
    /** `count` is a roll's MAXIMUM (e.g. 1d4 → 4), not the exact number summoned. */
    countRolled?: boolean;
    cast: () => void;
}

/** Parent-side lifecycle for an AOE whose placement surface lives in the FT3D iframe. */
export class FT3DAOEPlacement {
    active: FT3DActiveAOE | null = null;
    private version = 0;
    /** The L hotkey of a throw placement (Direct ⇄ Lob); aborted on cancel. */
    private throwKeys: AbortController | null = null;

    startTeleport(request: TeleportPlacementRequest): void {
        this.cancel(true);
        const version = ++this.version;
        const button = document.createElement('button');
        button.dataset.ability_id = request.abilityId;
        const envelope: Ft3dAoePlacementEnvelope = {
            schema: FT3D_AOE_PLACEMENT_SCHEMA, version,
            intent: {
                intentId: version, encounterId: request.encounterId, casterId: request.casterId,
                abilityId: request.abilityId, abilityName: request.name, placementKind: 'teleport',
                shape: 'cylinder', distanceFt: request.sizeFt / 2, heightFt: request.sizeFt, depthFt: request.sizeFt, rangeFt: request.rangeFt,
                selfRange: false, persistent: false, followsTarget: false, groundOnly: false,
                abilityDoesNotEffectInventory: true, excludesCaster: true,
                upcastable: false, baseCastLevel: 0, castLevel: 0, maxCastLevel: 0, upcastIncreaseFt: 0,
                ...(request.controllerId ? { controllerId: request.controllerId } : {}),
            },
        };
        this.active = { button, envelope, staged: null, castOverride: request.cast, teleportOfferId: request.offerId };
        window.ftAoeActiveAbilityId = request.abilityId;
        document.body.classList.add('ft3d-aoe-placement-active');
        window.dispatchEvent(new CustomEvent('ft:ft3d-aoe-placement-intent', { detail: envelope }));
    }

    /** Arm a GM creature drop: the summon ghost follows the pointer and a scene click commits the floor point,
     * which goes straight to the add-creature insert. Nothing is cast and there is no caster — so no range,
     * line of effect or sight (the scene's 'creature' branch skips them all). */
    startCreature(request: Ft3dCreaturePlacementRequest): boolean {
        const encounterId = String(request.encounterId || window.ftGetActiveEncounterId?.() || '').trim().toUpperCase();
        const creatureId = String(request.creatureId || '').trim().toUpperCase();
        if (!encounterId || !checkIfValidUUID(creatureId)) return false;
        const widthFt = Math.max(1, Number(request.widthFt) || 5);
        const heightFt = Math.max(1, Number(request.heightFt) || widthFt);
        this.cancel(true);
        const version = ++this.version;
        const name = String(request.name || 'Creature').trim() || 'Creature';
        const envelope: Ft3dAoePlacementEnvelope = {
            schema: FT3D_AOE_PLACEMENT_SCHEMA, version,
            intent: {
                intentId: version, encounterId, casterId: '', abilityId: '', abilityName: name,
                // A cylinder gives the shared renderer the creature's floor footprint and full height.
                shape: 'cylinder', distanceFt: widthFt / 2, heightFt, depthFt: widthFt, rangeFt: null,
                selfRange: false, persistent: false, followsTarget: false, groundOnly: true,
                abilityDoesNotEffectInventory: true, excludesCaster: false,
                upcastable: false, baseCastLevel: 0, castLevel: 0, maxCastLevel: 0, upcastIncreaseFt: 0,
                placementKind: 'creature', summonCreatureId: creatureId, summonName: name,
                summonPictureUrl: String(request.pictureUrl || '').trim(), summonWidthFt: widthFt, summonHeightFt: heightFt,
            },
        };
        this.active = {
            button: document.createElement('button'), envelope, staged: null,
            castOverride: request.place, onCancel: request.onCancel ?? null,
        };
        window.ftAoeActiveAbilityId = null;
        document.body.classList.add('ft3d-aoe-placement-active');
        window.dispatchEvent(new CustomEvent('ft:ft3d-aoe-placement-intent', { detail: envelope }));
        return true;
    }

    /** MV2 S1b: a GM moving an already-placed persistent zone with the placement tool (move / lift / rotate / aim). The zone
     *  starts at its stored span; the confirm reads the staged span and commits it through window.ftTryCommitZoneMove — the
     *  client-truth zone commit (websocket/zone-truth.ts), never a cast. */
    startZoneEdit(request: Ft3dZoneEditRequest): boolean {
        const encounterId = String(request.encounterId || '').trim().toUpperCase();
        const statusId = String(request.statusId || '').trim().toUpperCase();
        if (!encounterId || !checkIfValidUUID(statusId) || !(request.distanceFt > 0)) return false;
        this.cancel(true);
        const version = ++this.version;
        const envelope: Ft3dAoePlacementEnvelope = {
            schema: FT3D_AOE_PLACEMENT_SCHEMA, version,
            intent: {
                intentId: version, encounterId, casterId: '', abilityId: '', abilityName: String(request.name || 'Zone'),
                shape: request.shape, distanceFt: request.distanceFt, heightFt: Math.max(0.1, request.heightFt || 1), depthFt: Math.max(0.1, request.depthFt || 1),
                rangeFt: null, selfRange: false, persistent: true, followsTarget: false, groundOnly: request.shape === 'circle' || request.shape === 'square',
                abilityDoesNotEffectInventory: false, excludesCaster: false,
                upcastable: false, baseCastLevel: 0, castLevel: 0, maxCastLevel: 0, upcastIncreaseFt: 0,
                placementKind: 'zone-edit', editStatusId: statusId, editSpan: { origin: { ...request.origin }, endpoint: { ...request.endpoint } },
            },
        };
        const active: FT3DActiveAOE = { button: document.createElement('button'), envelope, staged: null, onCancel: request.onCancel ?? null };
        active.castOverride = () => {
            const staged = active.staged;
            const coords = String(staged?.targetSelection ?? '').split(',')[0]?.split('|')[1]?.split(':').map(Number) ?? [];
            const span = coords.length === 6 && coords.every(Number.isFinite)
                ? { ox: coords[0]!, oy: coords[1]!, oz: coords[2]!, ex: coords[3]!, ey: coords[4]!, ez: coords[5]! }
                : staged ? { ox: staged.origin.x, oy: staged.origin.y, oz: staged.origin.z, ex: staged.endpoint.x, ey: staged.endpoint.y, ez: staged.endpoint.z } : null;
            if (span) void window.ftTryCommitZoneMove?.({ encounterId, statusId, span });
        };
        this.active = active;
        window.ftAoeActiveAbilityId = null;
        document.body.classList.add('ft3d-aoe-placement-active');
        window.dispatchEvent(new CustomEvent('ft:ft3d-aoe-placement-intent', { detail: envelope }));
        return true;
    }

    start(button: HTMLElement, preservePrecast = false): boolean {
        delete window.ftTakePaintedWall;
        const aoeType = String(button.dataset.aoe_type || '');
        const details = AOETypes.find(type => type.item_id === aoeType);
        const shape = String(details?.variable_name || '').toLowerCase() as Ft3dAoeShape;
        const rawDistanceFt = Number(button.dataset.aoe_distance);
        const rawHeightFt = Math.max(1, Number(button.dataset.aoe_distance_height) || 1);
        const rawDepthFt = Math.max(1, Number(button.dataset.aoe_distance_depth) || 1);
        const encounterId = String(window.ftGetActiveEncounterId?.() || '').trim().toUpperCase();
        const casterId = String(window.ftGetActiveCasterId?.() || '').trim().toUpperCase();
        const abilityId = String(button.dataset.ability_id || '').trim().toUpperCase();
        if (!details || !['sphere', 'circle', 'cylinder', 'cube', 'square', 'line', 'cone'].includes(shape)
            || !(rawDistanceFt > 0) || !encounterId || !casterId || !abilityId) {
            displayWarning('Invalid FT3D AOE configuration');
            return false;
        }

        // Placement owns an AOE-shaped pre-cast fold-up even when event capture order changes or the
        // button was launched by another UI surface. This bridge is a no-op outside the FT3D dock.
        window.ftActionBarArmAoeChoice?.(button);
        // A same-ability replacement updates its invocation without ending the observers' cue.
        this.cancel(true, preservePrecast);
        const rangeId = String(button.dataset.range || '');
        const rangeFt = rangeFtFromButton(rangeId, rangeTypes, casterId);
        const version = ++this.version;
        const nextSpell = casterNextSpellArea(button, casterId);
        const widenedPercent = nextSpell.percent;
        const isMenta = button.dataset.item_type === 'menta';
        const baseCastLevel = Math.max(0, Number(isMenta ? button.dataset.menta_level : button.dataset.spell_level) || 0);
        const upcastIncreaseFt = Math.max(0, Number(button.dataset.spell_upcast_increase_aoe_ft) || 0);
        const inputIdReference = String(button.dataset.input_id_reference || '');
        const upcastable = button.dataset.ftInputFamily === 'spell-level';
        const availableResource = Math.floor(Number(isMenta ? button.dataset.caster_psi : button.dataset.caster_mana));
        const maxCastLevel = upcastable && Number.isFinite(availableResource) && availableResource >= baseCastLevel
            ? availableResource : baseCastLevel;
        const selectedCastLevel = window.ftAbUpcastLevel?.get(abilityId);
        const castLevel = upcastable
            ? Math.max(baseCastLevel, Math.min(maxCastLevel, Number(selectedCastLevel) || baseCastLevel))
            : baseCastLevel;
        if (upcastable) window.ftAbUpcastLevel?.set(abilityId, castLevel);
        const distanceFt = widenAoeDimensionFt(
            rawDistanceFt + upcastIncreaseFt * Math.max(0, castLevel + nextSpell.boost - baseCastLevel),
            widenedPercent,
        );
        const heightFt = widenAoeDimensionFt(rawHeightFt, widenedPercent);
        const depthFt = widenAoeDimensionFt(rawDepthFt, widenedPercent);
        const envelope: Ft3dAoePlacementEnvelope = {
            schema: FT3D_AOE_PLACEMENT_SCHEMA,
            version,
            intent: {
                intentId: version,
                // Explicit, not implied (2026-09-29): the parent-side consumers — use-ability.ts's client-truth `placed` check and
                // ability-truth.ts placedReaders — test `placementKind === 'aoe'`; only the scene's parser defaulted a missing kind,
                // so every ordinary placed AOE used to fall to the legacy lane.
                placementKind: 'aoe',
                encounterId,
                casterId,
                abilityId,
                abilityName: String(button.dataset.bsOriginalTitle || button.title || button.textContent || 'Ability').trim(),
                shape,
                // Painted walls are on for everyone (owner 2026-09-23); the button's capability decides.
                ...(button.dataset.wall_attachment === 'surface' || button.dataset.wall_attachment === 'floating'
                    ? { wallAttachment: button.dataset.wall_attachment } : {}),
                distanceFt,
                heightFt,
                depthFt,
                rangeFt,
                reachRange: getRangeDistanceFromCatalog(rangeId, rangeTypes) === 'reach',
                selfRange: rangeId.toUpperCase() === FT_SELF_RANGE_ID,
                persistent: button.dataset.persistent_aoe === '1',
                followsTarget: button.dataset.aoe_follows_target === '1',
                groundOnly: details.ground_only === true || shape === 'circle' || shape === 'square',
                abilityDoesNotEffectInventory: button.dataset.ability_does_not_effect_inventory === '1',
                abilityOnlyAffectsInventory: button.dataset.ability_only_affects_inventory === '1',
                // Indirect fire (owner 2026-09-15). Directional shapes never arc; the save refuses the pairing,
                // and this guard keeps a stale row honest.
                arcingTrajectory: button.dataset.arcing_trajectory === '1' && shape !== 'line' && shape !== 'cone',
                // A hand throw (Throw Object) flies the flattest 45° arc; siege lobs keep the range-derived angle.
                arcFlat: button.dataset.arcing_trajectory === '1' && button.dataset.arcing_flat === '1',
                ...(button.dataset.ftUseAbilityVariable1 === 'throw_inventory' ? { thrownObjectSizeId: button.dataset.ftThrownObjectSizeId ?? '' } : {}),
                // Telekinesis (2026-10-07): the throw leaves from the floor prop, so the scene measures range / arc from it.
                ...(button.dataset.ftUseAbilityVariable1 === 'throw_inventory' && button.dataset.ftThrowLaunchEntityId
                    ? { launchEntityId: button.dataset.ftThrowLaunchEntityId } : {}),
                excludesCaster: ft3dAoeExcludesCaster(button),
                ...(Number(button.dataset.ftActionPointCost) > 0 ? { actionPointCost: Math.trunc(Number(button.dataset.ftActionPointCost)) } : {}),
                upcastable,
                baseCastLevel,
                castLevel,
                maxCastLevel,
                upcastIncreaseFt,
            },
        };
        this.active = { button, envelope, staged: null };
        const pool = button.dataset.ftInstantProjectilePool
            ? parseProjectilePool(JSON.parse(button.dataset.ftInstantProjectilePool))
            : window.ftGetProjectilePool?.(casterId, abilityId);
        if (pool && envelope.intent) {
            if (pool.remaining < 1) { this.active = null; displayWarning('No projectiles remaining.'); return false; }
            envelope.intent.projectilePool = { ...pool, assigned: 0 };
            this.active.projectiles = [];
        }
        window.ftAoeActiveAbilityId = abilityId;
        document.body.classList.add('ft3d-aoe-placement-active');
        window.dispatchEvent(new CustomEvent('ft:ft3d-aoe-placement-intent', { detail: { ...envelope, resetPlacement: true } }));
        this.armThrowModeHotkey(button);
        window.ftActionBarRepaintAll?.();
        return true;
    }

    /** ONE Direct/Lob toggle for every throw (owner 2026-09-15): while a Throw Object placement is armed, the L key
     *  flips the flight between the flat 45° lob (default for objects) and a straight throw; the scene re-traces
     *  from the re-dispatched intent, and the cast posts the choice as `throw_lob` (Janus re-judges it). */
    private armThrowModeHotkey(button: HTMLElement): void {
        this.throwKeys?.abort();
        this.throwKeys = null;
        if (String(button.dataset.ftUseAbilityVariable1 || '') !== 'throw_inventory') return;
        const keys = new AbortController();
        this.throwKeys = keys;
        // The aim lives in the scene iframe, so the key usually lands there: listen on both documents (same origin).
        const sceneDocument = document.querySelector<HTMLIFrameElement>('[data-ft3d-encounter-frame]')?.contentDocument ?? null;
        const documents: Document[] = sceneDocument && sceneDocument !== document ? [document, sceneDocument] : [document];
        for (const doc of documents) doc.addEventListener('keydown', event => {
            if (event.code !== 'KeyL' || event.repeat || event.altKey || event.ctrlKey || event.metaKey) return;
            const target = event.target as HTMLElement | null;
            if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
            const active = this.active;
            if (!active?.envelope.intent || active.button !== button) return;
            event.preventDefault();
            const lob = !active.envelope.intent.arcingTrajectory;
            active.envelope = {
                ...active.envelope,
                version: ++this.version,
                intent: { ...active.envelope.intent, intentId: this.version, arcingTrajectory: lob, arcFlat: lob },
            };
            active.staged = null;
            window.dispatchEvent(new CustomEvent('ft:ft3d-aoe-placement-intent', { detail: active.envelope }));
            window.displayWarning?.(lob ? 'Lob — the throw arcs over cover (L: straight)' : 'Straight throw — needs a clear line (L: lob)');
        }, { signal: keys.signal });
    }

    /** Arm a ranged ground point for a summon. This deliberately reuses the spatial placement surface,
     * but it is not an AOE: the scene collects no occupants and the target wire contains only the point. */
    startSummon(button: HTMLElement, request: Ft3dSummonPlacementRequest, preservePrecast = false): boolean {
        const encounterId = String(window.ftGetActiveEncounterId?.() || '').trim().toUpperCase();
        const casterId = String(
            button.closest<HTMLElement>('[data-ft-caster-id]')?.dataset.ftCasterId
            || window.ftGetActiveCasterId?.()
            || '',
        ).trim().toUpperCase();
        const abilityId = String(button.dataset.ability_id || '').trim().toUpperCase();
        const creatureId = String(request.creatureId || '').trim().toUpperCase();
        const widthFt = Math.max(1, Number(request.widthFt) || 5);
        const heightFt = Math.max(1, Number(request.heightFt) || widthFt);
        if (!encounterId || !casterId || !abilityId || !checkIfValidUUID(creatureId)) {
            displayWarning('Invalid FT3D summon placement configuration');
            return false;
        }

        this.cancel(true, preservePrecast);
        const rangeId = String(button.dataset.range || '');
        const rangeFt = rangeFtFromButton(rangeId, rangeTypes, casterId);
        const version = ++this.version;
        const envelope: Ft3dAoePlacementEnvelope = {
            schema: FT3D_AOE_PLACEMENT_SCHEMA,
            version,
            intent: {
                intentId: version,
                encounterId,
                casterId,
                abilityId,
                abilityName: String(button.dataset.bsOriginalTitle || button.title || button.textContent || 'Summon').trim(),
                // A cylinder gives the shared renderer the right floor footprint and full creature height.
                shape: 'cylinder',
                distanceFt: widthFt / 2,
                heightFt,
                depthFt: widthFt,
                rangeFt,
                reachRange: getRangeDistanceFromCatalog(rangeId, rangeTypes) === 'reach',
                selfRange: rangeId.toUpperCase() === FT_SELF_RANGE_ID,
                persistent: false,
                followsTarget: false,
                groundOnly: true,
                abilityDoesNotEffectInventory: true,
                excludesCaster: false,
                upcastable: false,
                baseCastLevel: 0,
                castLevel: 0,
                maxCastLevel: 0,
                upcastIncreaseFt: 0,
                placementKind: 'summon',
                summonCreatureId: creatureId,
                summonName: String(request.name || 'Summon').trim() || 'Summon',
                summonPictureUrl: String(request.pictureUrl || '').trim(),
                summonWidthFt: widthFt,
                summonHeightFt: heightFt,
            },
        };
        this.active = { button, envelope, staged: null, castOverride: request.cast };
        const summonCount = Math.max(1, Math.min(FT_SUMMON_POINTS_MAX, Math.trunc(Number(request.count) || 1)));
        if (summonCount > 1) {
            this.active.summonPoints = [];
            this.active.summonCount = summonCount;
            this.active.summonRolled = request.countRolled === true;
        }
        window.ftSummonPoints = null;
        window.ftAoeActiveAbilityId = abilityId;
        document.body.classList.add('ft3d-aoe-placement-active');
        window.dispatchEvent(new CustomEvent('ft:ft3d-aoe-placement-intent', { detail: { ...envelope, resetPlacement: true } }));
        window.ftActionBarRepaintAll?.();
        return true;
    }

    setUpcastLevel(abilityId: string, level: number): void {
        const active = this.active;
        const intent = active?.envelope.intent;
        if (!active || !intent || intent.abilityId !== String(abilityId).toUpperCase()) return;
        const baseDistance = Number(active.button.dataset.aoe_distance);
        const baseLevel = intent.baseCastLevel;
        const increaseFt = intent.upcastIncreaseFt;
        if (!(baseDistance > 0) || !intent.upcastable) return;
        const castLevel = Math.max(baseLevel, Math.min(intent.maxCastLevel, Number(level) || baseLevel));
        const nextSpell = casterNextSpellArea(active.button, intent.casterId);
        const widenedPercent = nextSpell.percent;
        const distanceFt = widenAoeDimensionFt(
            baseDistance + increaseFt * Math.max(0, castLevel + nextSpell.boost - baseLevel),
            widenedPercent,
        );
        if (distanceFt === intent.distanceFt && castLevel === intent.castLevel) return;
        const version = ++this.version;
        active.envelope = {
            ...active.envelope,
            version,
            intent: { ...intent, intentId: version, distanceFt, castLevel },
        };
        active.staged = null;
        window.dispatchEvent(new CustomEvent('ft:ft3d-aoe-placement-intent', { detail: active.envelope }));
    }

    stage(payload: unknown): boolean {
        const stage = parseFt3dAoePlacementStageEnvelope(payload);
        if (!stage || !this.active || stage.version !== this.active.envelope.version) return false;
        const active = this.active;
        if (JSON.stringify(active.staged) === JSON.stringify(stage)) return true;
        active.staged = stage;
        // The exact center, not a rounded one: PHP's bounds tolerance is 0.01 ft, so a rounded key could keep a stale
        // refusal across that threshold.
        const key = JSON.stringify([stage.version, stage.targetSelection, stage.inRange, stage.lineOfEffect, stage.landingFailure ?? null, stage.aimFailure ?? null,
            stage.center.x, stage.center.y, stage.center.z]);
        if (active.placementCheck?.key !== key) {
            const message = placementFailure(active, stage);
            active.placementCheck = { key, state: message === null ? 'valid' : 'invalid', message: message ?? '' };
        }
        window.ftSetTargetSelection?.(stage.placed !== false ? stage.targetSelection : '', 'ft3d-aoe-placement');
        window.ftActionBarSetAoeTargets?.(active.envelope.intent?.abilityId ?? '', stage.targets);
        window.ftActionBarRepaintAll?.();
        return true;
    }

    /** Re-draw a multi-summon placement with its banked spots (drawn as the numbered impact ghosts). */
    private dispatchSummonPoints(active: FT3DActiveAOE, points: Ft3dAoePoint[]): void {
        const intent = active.envelope.intent;
        if (!intent) return;
        active.summonPoints = points;
        const version = ++this.version;
        active.envelope = { ...active.envelope, version, intent: {
            ...intent, intentId: version, projectileImpacts: points.map(point => ({ ...point })),
        } };
        active.staged = null;
        window.dispatchEvent(new CustomEvent('ft:ft3d-aoe-placement-intent', { detail: active.envelope }));
        window.ftActionBarRepaintAll?.();
    }

    addProjectile(removeLast = false): void {
        const active = this.active;
        if (active?.summonPoints) {
            // The action bar's Undo on a multi-summon placement takes back the last banked spot.
            if (removeLast && active.summonPoints.length) this.dispatchSummonPoints(active, active.summonPoints.slice(0, -1));
            return;
        }
        const intent = active?.envelope.intent;
        const pool = intent?.projectilePool;
        if (!active?.projectiles || !intent || !pool) return;
        if (removeLast) active.projectiles.pop();
        else {
            const stage = active.staged;
            if (!stage || stage.placed === false || active.placementCheck?.state !== 'valid' || !stage.inRange || stage.lineOfEffect === 'blocked' || active.projectiles.length >= pool.remaining) return;
            active.projectiles.push(stage);
        }
        const version = ++this.version;
        active.envelope = { ...active.envelope, version, intent: {
            ...intent, intentId: version, projectilePool: { ...pool, assigned: active.projectiles.length },
            projectileImpacts: active.projectiles.map(stage => ({ ...stage.center })),
        } };
        active.staged = null;
        window.dispatchEvent(new CustomEvent('ft:ft3d-aoe-placement-intent', { detail: active.envelope }));
        window.ftActionBarRepaintAll?.();
    }

    confirm(): void {
        const active = this.active;
        if (active?.projectiles) {
            const intent = active.envelope.intent;
            if (!intent || !active.projectiles.length) return;
            const assignments: ProjectileAssignment[] = active.projectiles.map(stage => ({ targetSelection: stage.targetSelection,
                propagation: stage.propagation, placementProof: { ...stage, ...intent } }));
            this.cancel(); // Consume before dispatch, including duplicate confirmation gestures.
            void window.ftUseProjectileAssignments?.(intent.casterId, intent.abilityId, assignments, intent.projectilePool?.instant);
            return;
        }
        // Invalid aim is already explained inline. Shortcuts and double-clicks obey the same
        // preflight as the disabled Confirm control; they never emit a warning or dispatch a cast.
        if (!active?.staged || active.staged.placed === false || active.placementCheck?.state !== 'valid'
            || !active.staged.inRange || active.staged.lineOfEffect === 'blocked') return;
        if (active.summonPoints && active.summonCount && active.envelope.intent) {
            if (this.summonSpotTaken(active, active.staged.center)) {
                displayWarning('Another summon already stands there · choose another spot');
                return;
            }
            const center = active.staged.center;
            const points = [...active.summonPoints, { x: center.x, y: center.y, z: center.z }];
            if (points.length < active.summonCount) {
                this.dispatchSummonPoints(active, points);
                return;
            }
            this.armSummonPoints(active, points);
        }
        this.commit(active);
    }

    /** Does a square footprint at `center` overlap a spot already banked for this multi-summon placement? */
    private summonSpotTaken(active: FT3DActiveAOE, center: Ft3dAoePoint): boolean {
        const intent = active.envelope.intent;
        const widthFt = Math.max(1, Number(intent?.summonWidthFt) || 5);
        const heightFt = Math.max(1, Number(intent?.summonHeightFt) || widthFt);
        return (active.summonPoints ?? []).some(point => Math.abs(point.x - center.x) < widthFt - 0.01
            && Math.abs(point.y - center.y) < widthFt - 0.01 && Math.abs(point.z - center.z) < heightFt);
    }

    /** Read once by queryAbilityData (use-ability.ts) for this caster + ability; Janus seats summon N on point N. */
    private armSummonPoints(active: FT3DActiveAOE, points: Ft3dAoePoint[]): void {
        const intent = active.envelope.intent;
        if (!intent) return;
        window.ftSummonPoints = { casterId: intent.casterId, abilityId: intent.abilityId, points, atMs: Date.now() };
    }

    /** A ROLLED summon count (owner 2026-09-16): the caster banks spots up to the roll's maximum, then casts whenever
     *  ready. The roll uses the spots in order; any summon beyond them spreads a body-width apart around the cast point
     *  (janus_summon_desired_point). An unbanked, clear staged spot counts as one more. */
    castSummonsNow(): void {
        const active = this.active;
        if (!active?.summonRolled || !active.summonPoints || !active.summonCount) return;
        const staged = active.staged;
        const stagedValid = !!staged && staged.placed !== false && active.placementCheck?.state === 'valid'
            && staged.inRange && staged.lineOfEffect !== 'blocked';
        const points = [...active.summonPoints];
        if (staged && stagedValid && points.length < active.summonCount && !this.summonSpotTaken(active, staged.center)) {
            points.push({ x: staged.center.x, y: staged.center.y, z: staged.center.z });
        }
        if (!points.length) {
            displayWarning('Click a spot in range to cast from');
            return;
        }
        this.armSummonPoints(active, points);
        if (stagedValid && staged) {
            this.commit(active, staged.center);
            return;
        }
        // Banked spots only (re-staging after a bank is not guaranteed): the cast's own target point is the last
        // banked spot, which the stage already proved in range when it was banked.
        const anchor = points[points.length - 1]!;
        const encounterId = active.envelope.intent?.encounterId ?? '';
        window.ftSetTargetSelection?.(`${encounterId}|${anchor.x.toFixed(3)}:${anchor.y.toFixed(3)}:${anchor.z.toFixed(3)}`, 'ft3d-aoe-placement');
        active.staged = null;
        this.commit(active, anchor);
    }

    private commit(active: FT3DActiveAOE, center: Ft3dAoePoint | null = active.staged?.center ?? null): void {
        if (!center) return;
        this.active = null; // Re-entrant confirmation must not dispatch the same placement twice.
        active.committed = true;
        if (active.staged?.wallGeometry && active.envelope.intent) armPaintedWall({
            casterId: active.envelope.intent.casterId, abilityId: active.envelope.intent.abilityId,
            targetSelection: active.staged.targetSelection, geometry: active.staged.wallGeometry,
        });
        if (active.staged?.propagation && active.envelope.intent) armAoePropagation({
            casterId: active.envelope.intent.casterId, abilityId: active.envelope.intent.abilityId,
            targetSelection: active.staged.targetSelection, mask: active.staged.propagation,
        });
        try {
            if (active.castOverride) active.castOverride(center);
            else this.cast(active.button, active);
        } finally {
            if (!this.active) {
                this.active = active;
                this.cancel();
            }
        }
    }

    cancel(preserveActionBarChoice = false, preservePrecast = false): void {
        if (!this.active?.committed) delete window.ftTakePaintedWall;
        this.throwKeys?.abort();
        this.throwKeys = null;
        const prior = this.active;
        if (!prior?.committed) delete window.ftTakeAoePropagation;
        const hadActive = prior !== null;
        this.active = null;
        if (prior?.onCancel && !prior.committed) {
            try { prior.onCancel(); } catch (error) { console.error('FT3D placement onCancel failed', error); }
        }
        window.ftActionBarSetAoeTargets?.('', []);
        if (hadActive && !preservePrecast) {
            const envelope: Ft3dAoePlacementEnvelope = {
                schema: FT3D_AOE_PLACEMENT_SCHEMA,
                version: ++this.version,
                intent: null,
            };
            window.dispatchEvent(new CustomEvent('ft:ft3d-aoe-placement-intent', { detail: envelope }));
        }
        if (hadActive) cancelTargetDraft('ft3d-aoe-placement-cancel');
        window.ftAoeActiveAbilityId = null;
        document.body.classList.remove('ft3d-aoe-placement-active');
        if (!preserveActionBarChoice) window.ftActionBarClearChoice?.();
        window.ftActionBarRepaintAll?.();
    }

    private cast(button: HTMLElement, active: FT3DActiveAOE | null = null): void {
        const abilityId = String(button.dataset.ability_id || '');
        // A synthetic button may pin the cast's variables outright (Throw Object: variable_1 'throw_inventory',
        // variable_2 the thrown inventory id). The landing point rides the selection like any placed AOE.
        const pinnedVariable1 = String(button.dataset.ftUseAbilityVariable1 || '').trim();
        if (pinnedVariable1) {
            const pinnedVariable2 = String(button.dataset.ftUseAbilityVariable2 || '').trim();
            if (pinnedVariable1 === 'throw_inventory') {
                // The placement's current Direct/Lob choice rides the request (use-ability.ts throwLobFields).
                const intent = active?.envelope.intent ?? null;
                const lob = intent?.arcingTrajectory !== false;
                window.ftThrowMode = lob ? 'lob' : 'direct';
                // A world prop keeps its telekinetic presentation. Physical held throws present from their
                // accepted ability operation, so every viewer shares its identity and no refused cast throws.
                const center = active?.staged?.center;
                if (intent && center && intent.launchEntityId) {
                    currentFt3dSceneWindow()?.postMessage({
                        type: 'ft3d-campaign-throw-flight-v1',
                        payload: { schema: 'ft.ft3d.throw-flight.v1', encounterId: intent.encounterId, casterId: intent.casterId,
                            inventoryId: pinnedVariable2, compositionId: String(button.dataset.ftThrowCompositionId || ''),
                            landingFt: { x: center.x, y: center.y, z: center.z }, arcFlat: lob && intent.arcFlat === true, arcing: lob, rangeFt: intent.rangeFt },
                    }, window.location.origin);
                }
            }
            if (pinnedVariable2) useAbilityVariable2(abilityId, pinnedVariable1, pinnedVariable2);
            else (window as Window & { useAbilityVariable1?: (id: string, v1: string) => unknown }).useAbilityVariable1?.(abilityId, pinnedVariable1);
            return;
        }
        const spellType = String(button.dataset.spell_type || '');
        const nestedInventoryId = String(button.dataset.nestedInventoryId || '').trim();
        let inputIdReference = String(button.dataset.input_id_reference || '');
        const key = abilityId.toUpperCase();
        const ritualSet = window.ftAbRitualArmed;
        const ritualArmed = ritualSet?.has(key) === true;
        if (ritualArmed) ritualSet?.delete(key);

        const upcastLevel = window.ftAbUpcastLevel?.get(key);
        let upcastBridge: HTMLInputElement | null = null;
        if (upcastLevel != null && Number.isFinite(upcastLevel) && inputIdReference) {
            document.getElementById('ft_aoe_upcast_bridge_input')?.remove();
            upcastBridge = document.createElement('input');
            upcastBridge.type = 'hidden';
            upcastBridge.id = 'ft_aoe_upcast_bridge_input';
            upcastBridge.value = String(upcastLevel);
            document.body.appendChild(upcastBridge);
            inputIdReference = upcastBridge.id;
            window.ftAbUpcastLevel?.delete(key);
        }

        const optionValues = window.ftAbOptionValues?.get(key);
        const primaryPick = String(optionValues?.primary ?? '').trim();
        let optionBridge: HTMLInputElement | null = null;
        if (!upcastBridge && primaryPick && inputIdReference) {
            document.getElementById('ft_aoe_option_bridge_input')?.remove();
            optionBridge = document.createElement('input');
            optionBridge.type = 'hidden';
            optionBridge.id = 'ft_aoe_option_bridge_input';
            optionBridge.value = primaryPick;
            document.body.appendChild(optionBridge);
            inputIdReference = optionBridge.id;
        }

        const sangSelector = `select[data-ft-sanguine-dmg-for="${key}"]`;
        const originPane = button.closest('.ft-pc-sheet-pane');
        const sangPick = String(
            document.querySelector<HTMLSelectElement>(`.ft-action-choicebar ${sangSelector}`)?.value
            ?? originPane?.querySelector<HTMLSelectElement>(sangSelector)?.value
            ?? document.querySelector<HTMLSelectElement>(sangSelector)?.value
            ?? '',
        ).trim();
        let sangBridge: HTMLInputElement | null = null;
        if (sangPick) {
            document.getElementById('ft_aoe_sanguine_dmg_bridge_input')?.remove();
            sangBridge = document.createElement('input');
            sangBridge.type = 'hidden';
            sangBridge.id = 'ft_aoe_sanguine_dmg_bridge_input';
            sangBridge.value = sangPick;
            document.body.appendChild(sangBridge);
        }

        const secondaryPick = String(optionValues?.secondary ?? '').trim();
        let secondaryBridge: HTMLInputElement | null = null;
        if (secondaryPick) {
            document.getElementById('ft_aoe_secondary_option_bridge_input')?.remove();
            secondaryBridge = document.createElement('input');
            secondaryBridge.type = 'hidden';
            secondaryBridge.id = 'ft_aoe_secondary_option_bridge_input';
            secondaryBridge.value = secondaryPick;
            document.body.appendChild(secondaryBridge);
        }

        const consumeInventoryId = nestedInventoryId && checkIfValidUUID(nestedInventoryId)
            ? nestedInventoryId : null;
        // Grant and charged casts keep their source in variable_2, just like the sheet invocation.
        // A grant's ordinary spell-level input must never replace that provenance.
        const castSourceId = (isSpellGrantMode(spellType) || spellType === 'charged_spell' || spellType === 'charged_ability')
            ? String(button.dataset.source || '').trim() : '';
        const castSource = castSourceId !== '' && checkIfValidUUID(castSourceId) ? castSourceId : null;
        const primaryValue = upcastLevel != null && Number.isFinite(upcastLevel)
            ? String(upcastLevel) : primaryPick;

        if (ritualArmed) {
            const ritualChoice = upcastLevel != null ? secondaryPick : primaryPick;
            if (ritualChoice) useAbilityVariable2(abilityId, 'spell_ritual', ritualChoice, consumeInventoryId);
            else useAbilityVariable1(abilityId, 'spell_ritual', consumeInventoryId);
        } else if (consumeInventoryId && secondaryPick && primaryValue && !spellType) {
            useAbilityVariable2(abilityId, primaryValue, secondaryPick, consumeInventoryId);
        } else if (consumeInventoryId && primaryValue && spellType) {
            useAbilityVariable2(abilityId, spellType, primaryValue, consumeInventoryId);
        } else if (consumeInventoryId && primaryValue) {
            useAbilityVariable1(abilityId, primaryValue, consumeInventoryId);
        } else if (consumeInventoryId) {
            useAbilityVariable2(abilityId, 'nested_inventory_source', consumeInventoryId);
        } else if (castSource) {
            useAbilityVariable2(abilityId, spellType, castSource);
        } else if (isSpellGrantMode(spellType)) {
            useAbilityVariable1(abilityId, spellType);
        } else if (secondaryPick && !spellType && inputIdReference) {
            useAbilityInput1Input2(abilityId, inputIdReference, secondaryBridge!.id);
        } else if (sangPick && !spellType && inputIdReference) {
            useAbilityInput1Input2(abilityId, inputIdReference, sangBridge!.id);
        } else if (sangPick && !spellType) useAbilityVariable2(abilityId, '', sangPick);
        else if (spellType && inputIdReference) useAbilityVariable1Input1(abilityId, spellType, inputIdReference);
        else if (spellType) useAbilityVariable1(abilityId, spellType);
        else if (inputIdReference) useAbilityInput1(abilityId, inputIdReference);
        else void useAbility(abilityId);
        upcastBridge?.remove();
        optionBridge?.remove();
        sangBridge?.remove();
        secondaryBridge?.remove();
        window.ftAbOptionValues?.delete(key);
    }
}
